import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Loader2, MailCheck, Send } from "lucide-react";
import { AuthShell, inputCls } from "@/components/AuthShell";
import { formatApiErrorDetail } from "@/context/AuthContext";
import { useSEO } from "@/components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForgotPasswordPage() {
  useSEO({ title: "Forgot Password — Loan Salaah", description: "Reset your Loan Salaah account password." });
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell
      eyebrow="Password reset"
      title="We'll email you a reset link."
      subtitle="Enter the email you registered with. The link stays valid for one hour."
      footer={<>Remembered it? <Link to="/login" data-testid="forgot-login-link" className="font-semibold text-concordia underline decoration-brass/50 underline-offset-2">Back to sign in</Link></>}
    >
      {sent ? (
        <div data-testid="forgot-success" className="mt-8 rounded-2xl border border-sage/40 bg-sage/10 p-6 text-center">
          <MailCheck className="mx-auto h-8 w-8 text-concordia" />
          <p className="mt-4 text-sm font-semibold text-charcoal">Check your inbox</p>
          <p className="mt-1 text-xs leading-relaxed text-slate">
            If an account exists for {email}, a reset link is on its way. It expires in one hour.
          </p>
        </div>
      ) : (
        <form data-testid="forgot-form" onSubmit={submit} className="mt-8 space-y-5" noValidate>
          <div>
            <label htmlFor="forgot-email-input" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate">Email</label>
            <input id="forgot-email-input" data-testid="forgot-email-input" type="email" className={inputCls} placeholder="you@example.com"
              value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          {error && <p data-testid="forgot-error" className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-xs text-red-700">{error}</p>}
          <button type="submit" data-testid="forgot-submit-button" disabled={busy}
            className="flex w-full items-center justify-center gap-2 rounded-full bg-concordia px-8 py-3.5 text-sm font-semibold text-ivory transition-all duration-300 hover:-translate-y-0.5 hover:bg-concordia-deep disabled:cursor-not-allowed disabled:opacity-60">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            {busy ? "Sending…" : "Email me a reset link"}
          </button>
        </form>
      )}
    </AuthShell>
  );
}
